import React, { useState } from 'react';
import AlertsTable from './AlertsTable';
import RegisteredUsers from './RegisteredUsers';
import OwnersTable from './OwnersTable';
import PreGeneratedQRCodes from './PreGeneratedQRCodes';
import { Grid, Card, CardContent, Typography, CardActionArea, Box, Avatar, Chip, Divider } from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PeopleIcon from '@mui/icons-material/People';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import QrCodeIcon from '@mui/icons-material/QrCode';
import StoreIcon from '@mui/icons-material/Store';
import BarChartIcon from '@mui/icons-material/BarChart';

const sections = [
  {
    id: 'vehicles',
    title: 'View Vehicles',
    icon: <DirectionsCarIcon fontSize="large" color="primary" />,
    desc: 'Manage and view all registered vehicles.',
    component: <RegisteredUsers />,
    tag: 'Live vehicle registry',
  },
  {
    id: 'owners',
    title: 'View Owners',
    icon: <PeopleIcon fontSize="large" color="primary" />,
    desc: 'See and manage vehicle owners.',
    component: <OwnersTable />,
    tag: 'Owner directory',
  },
  {
    id: 'alerts',
    title: 'View Alerts',
    icon: <NotificationsActiveIcon fontSize="large" color="primary" />,
    desc: 'Monitor all alerts sent to owners.',
    component: <AlertsTable />,
    tag: 'Latest alert activity',
  },
  {
    id: 'qr',
    title: 'Pre-generated QR Codes',
    icon: <QrCodeIcon fontSize="large" color="primary" />,
    desc: 'Manage new and linked pre-generated QR codes, and generate replacements when needed.',
    component: <PreGeneratedQRCodes />,
    tag: 'Pre-generated QR management',
  },
];

function AdminDashboard() {
  const [activeSection, setActiveSection] = useState('vehicles');
  const section = sections.find(item => item.id === activeSection) || sections[0];

  return (
    <Box sx={{ maxWidth: 1200, margin: 'auto', p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, textAlign: 'center' }}>
        Admin Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
        Click a panel below to open the corresponding data view. Registered vehicles, owner details, and alert activity are loaded from Firestore.
      </Typography>

      <Grid container spacing={3}>
        {sections.map(sectionItem => {
          const isActive = sectionItem.id === activeSection;
          return (
            <Grid item xs={12} sm={6} md={3} key={sectionItem.id}>
              <Card
                elevation={isActive ? 8 : 3}
                sx={{
                  borderRadius: 3,
                  height: '100%',
                  border: isActive ? theme => `2px solid ${theme.palette.primary.main}` : '1px solid rgba(0,0,0,0.08)',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardActionArea onClick={() => setActiveSection(sectionItem.id)} sx={{ height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center', py: 4, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <Box>
                      <Avatar sx={{ bgcolor: 'primary.light', width: 56, height: 56, margin: 'auto', mb: 2 }}>
                        {sectionItem.icon}
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{sectionItem.title}</Typography>
                      <Typography variant="body2" color="text.secondary">{sectionItem.desc}</Typography>
                    </Box>
                    <Chip label={sectionItem.tag} size="small" sx={{ mt: 3 }} />
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Divider sx={{ mt: 4, mb: 3 }} />
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{section.title}</Typography>
        <Typography variant="subtitle2" color="text.secondary">{section.tag}</Typography>
      </Box>
      {section.component}
    </Box>
  );
}

export default AdminDashboard;
